// API calls for the Insights pages ported from CGS2022.
//
// The endpoints are the ones the CGS pages call (GSWebAPIv2 InsightsController,
// MarketingInsightsController and ZendDeskController), with the same bodies. They accept this
// app's token because they're [Authorize(AuthenticationSchemes = "WEBAPI2, CGS2022AzureAD")],
// the same as TurnTimes, which this app already calls.
//
// This is a module of its own rather than more methods on api.service.js. That file is 4,000
// lines, and these calls only make sense together.
//
// Every call returns the API's envelope ({ isSuccess, data, message }) and throws a plain Error
// with the API's message when the call fails. That's the jqXHR.responseJSON.message CGS shows
// in its error dialog.

import axios from 'axios'
import auth from '@/authConfig.js'

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT

async function headers() {
  const tokenResponse = await auth.acquireTokenResponse()
  const token = tokenResponse?.accessToken
  return token ? { Authorization: 'Bearer ' + token } : {}
}

function failure(err) {
  const msg = err?.response?.data?.message || err?.response?.data?.Message || err?.message || 'Please try again'
  const e = new Error(msg)
  e.status = err?.response?.status
  e.cause = err
  return e
}

async function post(path, body) {
  try {
    const res = await axios.post(`${API_ENDPOINT}${path}`, body ?? {}, { headers: await headers() })
    return res.data
  } catch (err) {
    throw failure(err)
  }
}

async function get(path, params) {
  try {
    const res = await axios.get(`${API_ENDPOINT}${path}`, { headers: await headers(), params })
    return res.data
  } catch (err) {
    throw failure(err)
  }
}

// GetInsightHeader is the same for every page in a session (the user's branch, LO and BDA
// lists), so it is fetched once per acting user and shared.
const headerCache = new Map()

export function actingAzureId() {
  try { return localStorage.getItem('azure_id') || '' } catch (e) { return '' }
}

export default {
  // POST api/Insights/<action>: almost every insight. `action` is the controller action
  // name exactly as CGS calls it, e.g. 'GetActivePipelineAnalysisDataSet'.
  insights(action, body) {
    return post(`api/Insights/${action}`, body)
  },

  // GET api/Insights/GetLoan?loanId=…  (revenueAnalysisActiveLocksV2)
  getLoan(loanId) {
    return get('api/Insights/GetLoan', { loanId })
  },

  // GET api/Insights/GetInsightHeader/{azureId}: the filter bar's branch, LO and BDA lists.
  getInsightHeader(azureId = actingAzureId()) {
    if (!azureId) return Promise.reject(new Error('No signed-in user'))
    if (!headerCache.has(azureId)) {
      const p = get(`api/Insights/GetInsightHeader/${encodeURIComponent(azureId)}`)
      p.catch(() => headerCache.delete(azureId))
      headerCache.set(azureId, p)
    }
    return headerCache.get(azureId)
  },

  // api/MarketingInsights/<action>: Marketing Insights. `method` is 'GET' or 'POST', as CGS
  // calls each action.
  marketing(action, { method = 'POST', body, params } = {}) {
    return method === 'GET'
      ? get(`api/MarketingInsights/${action}`, params)
      : post(`api/MarketingInsights/${action}`, body)
  },

  // api/ZendDesk/<action>: Support Metrics (Ticket Insights in CGS).
  zendesk(action, { method = 'POST', body, params } = {}) {
    return method === 'GET'
      ? get(`api/ZendDesk/${action}`, params)
      : post(`api/ZendDesk/${action}`, body)
  },

  // For a call this module doesn't wrap yet: the same auth and error handling, with any path.
  raw: { get, post }
}
